import { Download, Eye } from "lucide-react";
import { useCallback, useMemo } from "react";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import type { ParsedSkin } from "../../../lib/spineParser";
import type { SpineTemplate } from "../../../types/spine-template";
import type { SKU } from "../../../types/sku";

/** 用 parsedSkins 建 skinName → category 映射（无 SKU 时回退） */
function buildSkinCategoryMap(parsedSkins: ParsedSkin[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const skin of parsedSkins) {
    map.set(skin.name, skin.category);
  }
  return map;
}

/** 从 store 中的 SKU 生成导出 JSON 项（仅 id，不拼 CDN URL） */
function buildExportSku(
  skuId: string,
  sku: Partial<SKU>,
  skinCategoryMap: Map<string, string>,
): SKU {
  const skinName = sku.skinName ?? "";
  const item: SKU = {
    id: skuId,
    pro: sku.pro ?? 0,
    skinName,
    category: sku.category || skinCategoryMap.get(skinName) || "",
  };
  if (sku.toningIds && sku.toningIds.length > 0) {
    item.toningIds = sku.toningIds;
  }
  if (sku.hidden) {
    item.hidden = true;
  }
  if (sku.originalAssetId != null && sku.originalAssetId !== "") {
    item.originalAssetId = sku.originalAssetId;
  }
  if (sku.thumbAssetId != null && sku.thumbAssetId !== "") {
    item.thumbAssetId = sku.thumbAssetId;
  }
  return item;
}

interface BuildParams {
  templateConfig: any;
  template: SpineTemplate | undefined;
  skinCategoryMap: Map<string, string>;
}

function mapToningColors(colors: any[]): any[] {
  return colors.map((colorSet: any) => {
    if (colorSet.light && colorSet.dark) {
      return {
        id: colorSet.id,
        light: colorSet.light,
        dark: colorSet.dark,
      };
    }
    const stops = (colorSet.colors || [])
      .slice()
      .sort((a: any, b: any) => a.offset - b.offset)
      .map((stop: any) => ({
        color: stop.color,
        offset: stop.offset,
      }));
    return {
      id: colorSet.id,
      colors: stops,
    };
  });
}

/**
 * 构建「导出预览 / 下载配置」用的 JSON 对象。
 * 与编辑器里 selection / sku 存的是同一套数据，导出时把 skuIds 展开成 skus 嵌套对象。
 */
function buildExportedAvatarConfig({
  templateConfig,
  template,
  skinCategoryMap,
}: BuildParams): any {
  const skus = templateConfig.skus || {};

  const selections = templateConfig.selection
    .slice()
    .sort((a: any, b: any) => (a.playIndex ?? 0) - (b.playIndex ?? 0))
    .map((sel: any) => {
      const skuIds = sel.skuIds || [];
      const row: any = {
        id: sel.id,
        playIndex: sel.playIndex,
        skus: skuIds.map((skuId: string) => {
          const sku = skus[skuId];
          return buildExportSku(skuId, sku ?? {}, skinCategoryMap);
        }),
      };
      if (sel.name) {
        row.name = sel.name;
      }
      if (sel.gid) {
        row.gid = sel.gid;
      }
      if (sel.background === 1) {
        row.background = 1;
      }
      if (sel.layerIconId) {
        row.cover = sel.layerIconId;
      }
      if (sel.hidden) {
        row.hidden = true;
      }
      // 与普通模版一致：opt true=可选，false=必选
      if (typeof sel.opt === "boolean") {
        row.opt = sel.opt;
      }
      return row;
    });

  const tonings = templateConfig.tonings.map((toning: any) => ({
    name: toning.name || "",
    id: toning.id,
    pro: toning.pro || 0,
    colors: mapToningColors(toning.colors),
  }));

  const animations = templateConfig.animations.map((anim: any) => ({
    id: anim.id,
    name: anim.name,
    pro: anim.pro || 0,
    loop: anim.loop,
  }));

  const exportConfig: any = {
    selections,
    tonings,
    animations,
    requireCategories: templateConfig.requireCategories || [],
  };

  if (template?.name) {
    exportConfig.name = template.name;
  }
  if (template?.preview) {
    exportConfig.preview = template.preview;
  }
  if (template?.type) {
    exportConfig.type = template.type;
  }
  if (
    template?.relatedTemplateIds &&
    template.relatedTemplateIds.length > 0
  ) {
    exportConfig.relatedTemplateIds = template.relatedTemplateIds;
  }

  return exportConfig;
}

export interface ExportConfigMenuItemsProps {
  templateConfig: any;
  template: SpineTemplate | undefined;
  parsedSkins: ParsedSkin[];
  currentTid: string | undefined;
  /** 导出预览：弹窗必须挂在下拉菜单外，由父组件渲染 ExportPreviewDialog */
  onExportPreviewJson: (json: string) => void;
}

export function ExportConfigMenuItems({
  templateConfig,
  template,
  parsedSkins,
  currentTid,
  onExportPreviewJson,
}: ExportConfigMenuItemsProps) {
  const skinCategoryMap = useMemo(
    () => buildSkinCategoryMap(parsedSkins),
    [parsedSkins],
  );

  const buildExportConfig = useCallback((): any | null => {
    if (!templateConfig) return null;
    return buildExportedAvatarConfig({
      templateConfig,
      template,
      skinCategoryMap,
    });
  }, [templateConfig, template, skinCategoryMap]);

  const handleExportPreview = () => {
    const exportConfig = buildExportConfig();
    if (exportConfig) {
      onExportPreviewJson(JSON.stringify(exportConfig, null, 2));
    }
  };

  const handleExportConfig = () => {
    const exportConfig = buildExportConfig();
    if (!exportConfig) return;

    const jsonStr = JSON.stringify(exportConfig, null, 2);
    const blob = new Blob([jsonStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `neku-${currentTid || "config"}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <DropdownMenuItem
        onClick={handleExportPreview}
        disabled={!templateConfig}
      >
        <Eye className="h-4 w-4 mr-2" />
        导出预览
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={handleExportConfig}
        disabled={!templateConfig}
      >
        <Download className="h-4 w-4 mr-2" />
        导出配置
      </DropdownMenuItem>
    </>
  );
}
